import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { apiService } from '../../../services/api';
import { Produto } from '../../../types/produtos';

registerLocale('pt-BR', ptBR);

const strToDate = (s: string) => s ? new Date(s + 'T00:00:00') : null;
const dateToStr = (d: Date | null) => d ? d.toISOString().slice(0, 10) : '';

// ─── Types ──────────────────────────────────────────────────────────────────

interface HistoricoRow {
  po_id: number;
  variante_id: number;
  numero_edital: string | null;
  po_quantidade: string;
  valor_referencia: string;
  po_valor_custo: string | null;
  po_melhor_orcado: string | null;
  po_valor_proposta: string | null;
  custo_logistico: string;
  custos_adicionais: string;
  percentual_imposto: string;
  margem_lucro: string | null;
  po_marca: string | null;
  descricao_especifica: string | null;
  unidade_medida_codigo: string | null;
  unidade_medida_nome: string | null;
  unidade_medida_abrev: string | null;
  oportunidade_id: number;
  oportunidade_titulo: string;
  oportunidade_status: string;
  data_licitacao: string | null;
  data_fechamento: string | null;
  numero_contrato: string | null;
  cotacao_id: number | null;
  cotacao_quantidade: string | null;
  cotacao_valor_unitario: string | null;
  cotacao_valor_total: string | null;
  cotacao_marca: string | null;
  cotacao_descricao: string | null;
  cotacao_observacoes: string | null;
  cotacao_data_criacao: string | null;
  orcamento_id: number | null;
  orcamento_versao: number | null;
  orcamento_titulo: string | null;
  orcamento_status: string | null;
  orcamento_data_validade: string | null;
  orcamento_data_criacao: string | null;
  fornecedor_id: number | null;
  fornecedor_nome: string | null;
  fornecedor_email: string | null;
  fornecedor_telefone: string | null;
}

interface Cotacao {
  cotacao_id: number;
  cotacao_quantidade: string;
  cotacao_valor_unitario: string;
  cotacao_valor_total: string;
  cotacao_marca: string | null;
  cotacao_descricao: string | null;
  cotacao_observacoes: string | null;
  cotacao_data_criacao: string | null;
  orcamento_id: number;
  orcamento_versao: number;
  orcamento_titulo: string;
  orcamento_status: string;
  orcamento_data_validade: string | null;
  orcamento_data_criacao: string | null;
  fornecedor_id: number | null;
  fornecedor_nome: string | null;
  fornecedor_email: string | null;
  fornecedor_telefone: string | null;
}

interface ProdutoOportunidadeGroup {
  po_id: number;
  variante_id: number;
  numero_edital: string | null;
  po_quantidade: string;
  valor_referencia: string;
  po_melhor_orcado: string | null;
  po_valor_proposta: string | null;
  po_marca: string | null;
  descricao_especifica: string | null;
  unidade_medida_codigo: string | null;
  unidade_medida_nome: string | null;
  unidade_medida_abrev: string | null;
  cotacoes: Cotacao[];
}

interface OportunidadeGroup {
  oportunidade_id: number;
  oportunidade_titulo: string;
  oportunidade_status: string;
  data_licitacao: string | null;
  data_fechamento: string | null;
  produtos: ProdutoOportunidadeGroup[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 3 });

function fmtCurrency(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === '') return '—';
  const n = typeof val === 'number' ? val : parseFloat(val);
  return isNaN(n) ? '—' : fmt.format(n);
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('pt-BR'); } catch { return val; }
}

function statusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    'Em Andamento': 'bg-primary',
    'Ganha': 'bg-success',
    'Perdida': 'bg-danger',
    'Cancelada': 'bg-secondary',
    'Suspenso': 'bg-warning text-dark',
    'Rascunho': 'bg-light text-dark border',
    'Em Análise': 'bg-info text-dark',
    'Aprovado': 'bg-success',
    'Rejeitado': 'bg-danger',
    'Vencedor': 'bg-success',
  };
  return map[status] ?? 'bg-secondary';
}

const STATUS_ACCENT: Record<string, string> = {
  'Em Andamento': '#0d6efd',
  'Ganha':        '#198754',
  'Perdida':      '#dc3545',
  'Cancelada':    '#6c757d',
  'Suspenso':     '#ffc107',
  'Rascunho':     '#adb5bd',
  'Em Análise':   '#0dcaf0',
  'Aprovado':     '#198754',
  'Rejeitado':    '#dc3545',
  'Vencedor':     '#198754',
};

function groupRows(rows: HistoricoRow[]): OportunidadeGroup[] {
  const oppMap = new Map<number, OportunidadeGroup>();
  const poMap = new Map<number, ProdutoOportunidadeGroup>();

  for (const row of rows) {
    if (!oppMap.has(row.oportunidade_id)) {
      oppMap.set(row.oportunidade_id, {
        oportunidade_id: row.oportunidade_id,
        oportunidade_titulo: row.oportunidade_titulo,
        oportunidade_status: row.oportunidade_status,
        data_licitacao: row.data_licitacao,
        data_fechamento: row.data_fechamento,
        produtos: [],
      });
    }

    const opp = oppMap.get(row.oportunidade_id)!;

    if (!poMap.has(row.po_id)) {
      const poGroup: ProdutoOportunidadeGroup = {
        po_id: row.po_id,
        variante_id: row.variante_id,
        numero_edital: row.numero_edital,
        po_quantidade: row.po_quantidade,
        valor_referencia: row.valor_referencia,
        po_melhor_orcado: row.po_melhor_orcado,
        po_valor_proposta: row.po_valor_proposta,
        po_marca: row.po_marca,
        descricao_especifica: row.descricao_especifica,
        unidade_medida_codigo: row.unidade_medida_codigo,
        unidade_medida_nome: row.unidade_medida_nome,
        unidade_medida_abrev: row.unidade_medida_abrev,
        cotacoes: [],
      };
      poMap.set(row.po_id, poGroup);
      opp.produtos.push(poGroup);
    }

    if (row.cotacao_id !== null) {
      const po = poMap.get(row.po_id)!;
      if (!po.cotacoes.some(c => c.cotacao_id === row.cotacao_id)) {
        po.cotacoes.push({
          cotacao_id: row.cotacao_id!,
          cotacao_quantidade: row.cotacao_quantidade!,
          cotacao_valor_unitario: row.cotacao_valor_unitario!,
          cotacao_valor_total: row.cotacao_valor_total!,
          cotacao_marca: row.cotacao_marca,
          cotacao_descricao: row.cotacao_descricao,
          cotacao_observacoes: row.cotacao_observacoes,
          cotacao_data_criacao: row.cotacao_data_criacao,
          orcamento_id: row.orcamento_id!,
          orcamento_versao: row.orcamento_versao!,
          orcamento_titulo: row.orcamento_titulo!,
          orcamento_status: row.orcamento_status!,
          orcamento_data_validade: row.orcamento_data_validade,
          orcamento_data_criacao: row.orcamento_data_criacao,
          fornecedor_id: row.fornecedor_id,
          fornecedor_nome: row.fornecedor_nome,
          fornecedor_email: row.fornecedor_email,
          fornecedor_telefone: row.fornecedor_telefone,
        });
      }
    }
  }

  return Array.from(oppMap.values());
}

// ─── Summary (com fallback po_melhor_orcado) ────────────────────────────────

function calcSummary(groups: OportunidadeGroup[]) {
  const fornecedoresSet = new Set<string>();
  let totalCotacoes = 0;
  let min: number | null = null;
  let max: number | null = null;
  let minOppId: number | null = null;
  let maxOppId: number | null = null;

  for (const opp of groups) {
    for (const po of opp.produtos) {
      const valores: number[] = [];

      if (po.cotacoes.length === 0) {
        if (po.po_melhor_orcado) {
          const v = parseFloat(po.po_melhor_orcado);
          if (!isNaN(v)) valores.push(v);
        }
      } else {
        for (const c of po.cotacoes) {
          const v = parseFloat(c.cotacao_valor_unitario);
          if (!isNaN(v)) valores.push(v);
          if (c.fornecedor_nome) fornecedoresSet.add(c.fornecedor_nome);
          totalCotacoes++;
        }
      }

      for (const v of valores) {
        if (min === null || v < min) { min = v; minOppId = opp.oportunidade_id; }
        if (max === null || v > max) { max = v; maxOppId = opp.oportunidade_id; }
      }
    }
  }

  return { totalOportunidades: groups.length, totalCotacoes, totalFornecedores: fornecedoresSet.size, min, max, minOppId, maxOppId };
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface HistoricoPrecoModalProps {
  produto: Produto;
  onHide: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const HistoricoPrecoModal: React.FC<HistoricoPrecoModalProps> = ({ produto, onHide }) => {
  const [rows, setRows] = useState<HistoricoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filtroUnidade, setFiltroUnidade] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroFornecedor, setFiltroFornecedor] = useState('');
  const [filtroDataDe, setFiltroDataDe] = useState('');
  const [filtroDataAte, setFiltroDataAte] = useState('');
  const [filtroApenasComOrcamento, setFiltroApenasComOrcamento] = useState(true);

  const [openOpp, setOpenOpp] = useState<Set<number>>(new Set());
  const [highlightOppId, setHighlightOppId] = useState<number | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiService.getProdutoHistorico(produto.id)
      .then(res => {
        setRows(res.data ?? []);
        const ids = new Set<number>();
        (res.data ?? []).forEach((r: HistoricoRow) => ids.add(r.oportunidade_id));
        setOpenOpp(ids);
      })
      .catch(err => setError(err.message ?? 'Erro ao carregar histórico'))
      .finally(() => setLoading(false));
  }, [produto.id]);

  const unidades = useMemo(() => {
    const s = new Set<string>();
    rows.forEach(r => { if (r.unidade_medida_codigo) s.add(r.unidade_medida_codigo); });
    return Array.from(s).sort();
  }, [rows]);

  const statusOpp = useMemo(() => {
    const s = new Set<string>();
    rows.forEach(r => s.add(r.oportunidade_status));
    return Array.from(s).sort();
  }, [rows]);

  // Filtrar rows por unidade/status/fornecedor/data
  const filteredRows = useMemo(() => rows.filter(r => {
    if (filtroUnidade && r.unidade_medida_codigo !== filtroUnidade) return false;
    if (filtroStatus && r.oportunidade_status !== filtroStatus) return false;
    if (filtroFornecedor && !(r.fornecedor_nome ?? '').toLowerCase().includes(filtroFornecedor.toLowerCase())) return false;
    if (filtroDataDe && r.data_licitacao && r.data_licitacao.slice(0, 10) < filtroDataDe) return false;
    if (filtroDataAte && r.data_licitacao && r.data_licitacao.slice(0, 10) > filtroDataAte) return false;
    return true;
  }), [rows, filtroUnidade, filtroStatus, filtroFornecedor, filtroDataDe, filtroDataAte]);

  // Agrupar e depois aplicar filtro "apenas com orçamento" no nível de grupos
  const groups = useMemo(() => groupRows(filteredRows), [filteredRows]);

  const groupsFiltrados = useMemo(() => {
    if (!filtroApenasComOrcamento) return groups;
    return groups
      .map(opp => ({ ...opp, produtos: opp.produtos.filter(po => po.cotacoes.length > 0) }))
      .filter(opp => opp.produtos.length > 0);
  }, [groups, filtroApenasComOrcamento]);

  const summary = useMemo(() => calcSummary(groupsFiltrados), [groupsFiltrados]);

  // Unidades visíveis nos grupos exibidos (após filtro)
  const unidadesVisiveis = useMemo(() => {
    const s = new Set<string>();
    groupsFiltrados.forEach(opp =>
      opp.produtos.forEach(po => {
        const u = po.unidade_medida_abrev || po.unidade_medida_codigo;
        if (u) s.add(u);
      })
    );
    return Array.from(s).sort();
  }, [groupsFiltrados]);

  const navegarParaOpp = (oppId: number | null) => {
    if (!oppId) return;
    // Abre o accordion se estiver fechado
    setOpenOpp(prev => new Set(Array.from(prev).concat(oppId)));
    // Destaca e depois remove o highlight
    setHighlightOppId(oppId);
    setTimeout(() => setHighlightOppId(null), 2000);
    // Scroll até o card após o accordion abrir
    setTimeout(() => {
      const el = document.getElementById(`opp-card-${oppId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
  };

  const toggleOpp = (id: number) => {
    setOpenOpp(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setFiltroUnidade('');
    setFiltroStatus('');
    setFiltroFornecedor('');
    setFiltroDataDe('');
    setFiltroDataAte('');
  };

  const hasFilters = filtroUnidade || filtroStatus || filtroFornecedor || filtroDataDe || filtroDataAte;

  return (
    <>
    <style>{`
      .historico-scroll::-webkit-scrollbar { width: 8px; }
    `}</style>
    <Modal show onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton className="border-bottom py-3">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="ti ti-chart-bar fs-5 text-primary" />
          <div>
            <div className="fs-6 fw-semibold">Histórico de Preços</div>
            <div className="small text-muted fw-normal">{produto.nome}</div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <span className="spinner-border text-primary me-2" />
            <span>Carregando histórico...</span>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger m-3">
            <i className="ti ti-alert-circle me-2" />{error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-5 text-muted">
            <i className="ti ti-box-off fs-1 d-block mb-2" />
            <p className="mb-0">Este produto não foi adicionado a nenhuma oportunidade ainda.</p>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            {/* ── Summary Cards ── */}
            <div className="bg-light border-bottom px-4 py-3">
              <div className="row g-3">
                <SummaryCard value={summary.totalOportunidades} label="Oportunidades" color="text-primary" icon="ti-briefcase" />
                <SummaryCard value={summary.totalCotacoes} label="Cotações" color="text-secondary" icon="ti-receipt" />
                <SummaryCard value={fmtCurrency(summary.min)} label="Menor Preço" color="text-success" icon="ti-trending-down" onClick={() => navegarParaOpp(summary.minOppId)} />
                <SummaryCard value={fmtCurrency(summary.max)} label="Maior Preço" color="text-danger" icon="ti-trending-up" onClick={() => navegarParaOpp(summary.maxOppId)} />
                <SummaryCard value={summary.totalFornecedores} label="Fornecedores" color="text-warning" icon="ti-building-store" />
              </div>

            </div>

            {/* ── Filtros ── */}
            <div className="px-4 py-3 border-bottom bg-white">
              <div className="row g-2 align-items-end">
                <div className="col">
                  <label className="form-label small mb-1">Unidade</label>
                  <select className="form-select form-select-sm" value={filtroUnidade} onChange={e => setFiltroUnidade(e.target.value)}>
                    <option value="">Todas</option>
                    {unidades.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col">
                  <label className="form-label small mb-1">Status</label>
                  <select className="form-select form-select-sm" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                    <option value="">Todos</option>
                    {statusOpp.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col">
                  <label className="form-label small mb-1">Fornecedor</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text"><i className="ti ti-search" /></span>
                    <input type="text" className="form-control" placeholder="Buscar..." value={filtroFornecedor} onChange={e => setFiltroFornecedor(e.target.value)} />
                  </div>
                </div>
                <div className="col">
                  <label className="form-label small mb-1">Período</label>
                  <div className="d-flex gap-1">
                    <DatePicker
                      selected={strToDate(filtroDataDe)}
                      onChange={(d) => setFiltroDataDe(dateToStr(d))}
                      selectsStart
                      startDate={strToDate(filtroDataDe)}
                      endDate={strToDate(filtroDataAte)}
                      maxDate={strToDate(filtroDataAte) ?? undefined}
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      placeholderText="De"
                      className="form-control form-control-sm"
                    />
                    <DatePicker
                      selected={strToDate(filtroDataAte)}
                      onChange={(d) => setFiltroDataAte(dateToStr(d))}
                      selectsEnd
                      startDate={strToDate(filtroDataDe)}
                      endDate={strToDate(filtroDataAte)}
                      minDate={strToDate(filtroDataDe) ?? undefined}
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      placeholderText="Até"
                      className="form-control form-control-sm"
                    />
                  </div>
                </div>
                <div className="col-auto d-flex align-items-end pb-1">
                  <div className="form-check form-switch mb-0 text-nowrap">
                    <input className="form-check-input" type="checkbox" checked={filtroApenasComOrcamento} onChange={e => setFiltroApenasComOrcamento(e.target.checked)} id="chk-apenas-orcamento" />
                    <label className="form-check-label small" htmlFor="chk-apenas-orcamento">Apenas com orçamento</label>
                  </div>
                </div>
                {hasFilters && (
                  <div className="col-auto d-flex align-items-end">
                    <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters} title="Limpar filtros">
                      <i className="ti ti-x" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {groupsFiltrados.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-filter-off fs-2 d-block mb-2" />
                <p className="mb-1">Nenhum resultado para os filtros aplicados.</p>
                {filtroApenasComOrcamento && (
                  <button
                    className="btn btn-sm btn-outline-secondary mt-1"
                    onClick={() => setFiltroApenasComOrcamento(false)}
                  >
                    Mostrar também sem orçamento
                  </button>
                )}
              </div>
            ) : (
              <div ref={scrollContainerRef} className="px-3 py-3 historico-scroll" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {groupsFiltrados.map(opp => (
                  <OportunidadeAccordion
                    key={opp.oportunidade_id}
                    opp={opp}
                    isOpen={openOpp.has(opp.oportunidade_id)}
                    onToggle={() => toggleOpp(opp.oportunidade_id)}
                    globalMin={summary.min}
                    isHighlighted={highlightOppId === opp.oportunidade_id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top py-2">
        <button className="btn btn-secondary btn-sm" onClick={onHide}>Fechar</button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

// ─── Summary Card ─────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{ value: string | number; label: string; color: string; icon: string; onClick?: () => void }> = ({ value, label, color, icon, onClick }) => (
  <div className="col-6 col-md">
    <div
      className={`d-flex flex-column align-items-center text-center rounded py-1 ${onClick ? 'cursor-pointer' : ''}`}
      style={{ transition: 'background 0.15s', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.05)'; }}
      onMouseLeave={e => { if (onClick) (e.currentTarget as HTMLElement).style.background = ''; }}
      title={onClick ? 'Clique para ir até essa oportunidade' : undefined}
    >
      <i className={`ti ${icon} ${color} mb-1`} style={{ fontSize: '1.1rem' }} />
      <div className={`fw-bold ${color}`} style={{ fontSize: typeof value === 'string' && value.length > 6 ? '0.9rem' : '1.15rem' }}>
        {value}
      </div>
      <div className="small text-muted d-flex align-items-center gap-1">
        {label}
        {onClick && <i className="ti ti-click" style={{ fontSize: '0.7rem', opacity: 0.5 }} />}
      </div>
    </div>
  </div>
);

// ─── Accordion Item ───────────────────────────────────────────────────────────

interface OportunidadeAccordionProps {
  opp: OportunidadeGroup;
  isOpen: boolean;
  onToggle: () => void;
  globalMin: number | null;
  isHighlighted?: boolean;
}

const OportunidadeAccordion: React.FC<OportunidadeAccordionProps> = ({ opp, isOpen, onToggle, globalMin, isHighlighted }) => {
  const totalCotacoes = opp.produtos.reduce((acc, p) => acc + p.cotacoes.length, 0);
  const accent = STATUS_ACCENT[opp.oportunidade_status] ?? '#6c757d';

  return (
    <div
      id={`opp-card-${opp.oportunidade_id}`}
      className="card mb-3"
      style={{
        borderLeft: `4px solid ${accent}`,
        borderTop: `1px solid ${isHighlighted ? accent : '#dee2e6'}`,
        borderRight: `1px solid ${isHighlighted ? accent : '#dee2e6'}`,
        borderBottom: `1px solid ${isHighlighted ? accent : '#dee2e6'}`,
        boxShadow: isHighlighted ? `0 0 0 3px ${accent}40` : '0 1px 4px rgba(0,0,0,0.08)',
        borderRadius: '0.375rem',
        overflow: 'hidden',
        transition: 'box-shadow 0.3s, border-color 0.3s',
      }}
    >
      <div
        className="card-header d-flex align-items-center justify-content-between py-2 px-3"
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: `${accent}12`,
          borderBottom: isOpen ? `1px solid ${accent}30` : 'none',
        }}
        onClick={onToggle}
      >
        {/* Esquerda */}
        <div className="d-flex align-items-center gap-2 min-w-0">
          <i className={`ti ${isOpen ? 'ti-chevron-down' : 'ti-chevron-right'} text-muted flex-shrink-0`} />
          <a
            href={`/crm/oportunidades/detalhes/${opp.oportunidade_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="fw-semibold text-decoration-none text-truncate"
            style={{ maxWidth: 320, color: 'inherit', transition: 'color 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#0d6efd')}
            onMouseLeave={e => (e.currentTarget.style.color = 'inherit')}
            onClick={e => e.stopPropagation()}
            title="Abrir oportunidade em nova aba"
          >
            {opp.oportunidade_titulo}
            <i className="ti ti-external-link ms-1 text-muted" style={{ fontSize: '0.7rem' }} />
          </a>
          <span className={`badge flex-shrink-0 ${statusBadgeClass(opp.oportunidade_status)}`} style={{ fontSize: '0.72rem' }}>
            {opp.oportunidade_status}
          </span>
        </div>

        {/* Direita */}
        <div className="d-flex align-items-center gap-3 text-muted small ms-3 flex-shrink-0">
          {opp.data_licitacao && (
            <span><i className="ti ti-calendar me-1" />{fmtDate(opp.data_licitacao)}</span>
          )}
          <span className="badge bg-light text-secondary border">{totalCotacoes} cotaç{totalCotacoes === 1 ? 'ão' : 'ões'}</span>
        </div>
      </div>

      {isOpen && (
        <div className="card-body p-0">
          {opp.produtos.map((po, idx) => (
            <ProdutoOportunidadeSection
              key={po.po_id}
              po={po}
              isLast={idx === opp.produtos.length - 1}
              globalMin={globalMin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Produto Oportunidade Section ─────────────────────────────────────────────

interface ProdutoOportunidadeSectionProps {
  po: ProdutoOportunidadeGroup;
  isLast: boolean;
  globalMin: number | null;
}

const ProdutoOportunidadeSection: React.FC<ProdutoOportunidadeSectionProps> = ({ po, isLast, globalMin }) => {
  const menorCotacao = useMemo(() => {
    if (!po.cotacoes.length) return null;
    return po.cotacoes.reduce((min, c) => {
      const v = parseFloat(c.cotacao_valor_unitario);
      return !isNaN(v) && v < (min ?? Infinity) ? v : min;
    }, null as number | null);
  }, [po.cotacoes]);

  const unidade = po.unidade_medida_nome || po.unidade_medida_codigo || 'un';
  const temSecundarios = po.numero_edital || po.po_marca || po.descricao_especifica;

  // Fallback: se po_melhor_orcado não estiver preenchido, usa o menor valor das cotações
  const valorOrcadoExibido = po.po_melhor_orcado
    ?? (menorCotacao !== null ? String(menorCotacao) : null);

  return (
    <div className={`px-3 py-3 ${!isLast ? 'border-bottom' : ''}`}>

      {/* ── Info-bar principal ── */}
      <div
        className="d-flex align-items-center flex-wrap bg-light rounded px-3 py-2 mb-2"
        style={{ fontSize: '0.82rem', gap: '0 0.5rem' }}
      >
        {/* Badge unidade — em destaque */}
        <span className="badge bg-primary me-2" style={{ fontSize: '0.78rem', letterSpacing: '0.02em' }}>
          {unidade}
        </span>

        <span className="text-muted">
          Qtd: <strong className="text-body">{fmtNum.format(parseFloat(po.po_quantidade))}</strong>
        </span>

        <span className="text-muted px-2">│</span>

        <span className="text-muted">
          Ref: <strong className="text-body">{fmtCurrency(po.valor_referencia)}</strong>
        </span>

        {valorOrcadoExibido && (
          <>
            <span className="text-muted px-2">│</span>
            <span className="text-success">
              <i className="ti ti-star-filled me-1" style={{ fontSize: '0.7rem' }} />
              Orçado: <strong>{fmtCurrency(valorOrcadoExibido)}</strong>
            </span>
          </>
        )}

        {po.po_valor_proposta && (
          <>
            <span className="text-muted px-2">│</span>
            <span className="text-primary">
              <i className="ti ti-send me-1" style={{ fontSize: '0.7rem' }} />
              Proposta: <strong>{fmtCurrency(po.po_valor_proposta)}</strong>
            </span>
          </>
        )}

        {po.variante_id > 1 && (
          <span className="badge bg-info-subtle text-info ms-auto">Variante {po.variante_id}</span>
        )}
      </div>

      {/* ── Linha secundária (edital, marca, descrição) ── */}
      {temSecundarios && (
        <div className="d-flex flex-wrap gap-3 mb-2" style={{ fontSize: '0.78rem', color: '#6c757d' }}>
          {po.numero_edital && (
            <span><i className="ti ti-file-description me-1" />Edital: <strong>{po.numero_edital}</strong></span>
          )}
          {po.po_marca && (
            <span><i className="ti ti-tag me-1" />Marca: <strong>{po.po_marca}</strong></span>
          )}
          {po.descricao_especifica && (
            <span title={po.descricao_especifica}>
              <i className="ti ti-info-circle me-1" />
              {po.descricao_especifica.length > 60 ? po.descricao_especifica.slice(0, 60) + '…' : po.descricao_especifica}
            </span>
          )}
        </div>
      )}

      {/* ── Tabela de cotações ── */}
      {po.cotacoes.length === 0 ? (
        <div className="text-muted small fst-italic px-1">
          <i className="ti ti-receipt-off me-1" />
          Nenhum orçamento registrado para este item.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0 align-middle" style={{ fontSize: '0.82rem' }}>
            <thead className="table-light">
              <tr>
                <th>Fornecedor</th>
                <th>Orçamento</th>
                <th>Marca</th>
                <th className="text-end">Preço Unit.</th>
                <th className="text-end">Qtd</th>
                <th className="text-end">Total</th>
                <th>Status</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {po.cotacoes.map(c => {
                const valor = parseFloat(c.cotacao_valor_unitario);
                const isBest = !isNaN(valor) && menorCotacao !== null && valor === menorCotacao;
                const isGlobalBest = isBest && globalMin !== null && valor === globalMin;

                return (
                  <tr key={c.cotacao_id} className={isBest ? 'table-success' : ''}>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        {isBest && (
                          <i
                            className={`ti ti-trophy-filled ${isGlobalBest ? 'text-warning' : 'text-success'}`}
                            title={isGlobalBest ? 'Melhor preço global' : 'Melhor preço nesta oportunidade'}
                          />
                        )}
                        <span>{c.fornecedor_nome ?? <em className="text-muted">Não informado</em>}</span>
                      </div>
                      {c.fornecedor_email && (
                        <div className="text-muted" style={{ fontSize: '0.73rem' }}>{c.fornecedor_email}</div>
                      )}
                    </td>
                    <td>
                      <div className="fw-medium">{c.orcamento_titulo}</div>
                      <div className="text-muted" style={{ fontSize: '0.73rem' }}>v{c.orcamento_versao}</div>
                    </td>
                    <td>{c.cotacao_marca ?? '—'}</td>
                    <td className="text-end fw-bold">{fmtCurrency(c.cotacao_valor_unitario)}</td>
                    <td className="text-end">{c.cotacao_quantidade ? fmtNum.format(parseFloat(c.cotacao_quantidade)) : '—'}</td>
                    <td className="text-end">{fmtCurrency(c.cotacao_valor_total)}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(c.orcamento_status)}`} style={{ fontSize: '0.72rem' }}>
                        {c.orcamento_status}
                      </span>
                    </td>
                    <td>{fmtDate(c.cotacao_data_criacao)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
